import { useState } from "react";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/config/api";

const Contact = ({ config, content }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Default content
  const contactContent = content || {
    section_label: "Contacto",
    title: "Consulta sin compromiso",
    subtitle: "Cuenteme su caso y le proporcionare una evaluacion inicial gratuita. Respondo en menos de 24 horas.",
    form: {
      name_label: "Nombre completo",
      name_placeholder: "Su nombre",
      email_label: "Email",
      email_placeholder: "su@email.com",
      phone_label: "Telefono (opcional)",
      phone_placeholder: "su telefono",
      message_label: "Describa brevemente su caso",
      message_placeholder: "Cuentenos su situacion y como podemos ayudarle...",
      submit_button: "Enviar Consulta",
      privacy_text: "Al enviar este formulario acepta nuestra politica de privacidad."
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Mensaje enviado correctamente. Nos pondremos en contacto pronto.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Error al enviar el mensaje. Por favor, intentelo de nuevo.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-slate-800/30 relative" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left content */}
          <div>
            <span className="text-sky-400 font-mono text-sm tracking-wider uppercase mb-4 block">
              {contactContent.section_label}
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-6">
              {contactContent.title}
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed mb-8">
              {contactContent.subtitle}
            </p>

            {/* Contact info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-slate-800 border border-slate-700 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-sky-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <a 
                    href={`mailto:${config?.contact?.email || 'contacto@ciberperitoforense.es'}`}
                    className="text-white hover:text-sky-400 transition-colors"
                  >
                    {config?.contact?.email || 'contacto@ciberperitoforense.es'}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-slate-800 border border-slate-700 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-sky-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Telefono</p>
                  <a 
                    href={`tel:${config?.contact?.phone || '+34600000000'}`}
                    className="text-white hover:text-sky-400 transition-colors"
                  >
                    {config?.contact?.phone || ''}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-slate-800 border border-slate-700 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-sky-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Ubicacion</p>
                  <p className="text-white">{config?.contact?.location || 'Madrid, España'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right content - Form */}
          <div className="card-service" data-testid="contact-form-card">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">{contactContent.form.name_label}</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={contactContent.form.name_placeholder}
                  required
                  className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-600 focus:border-sky-400"
                  data-testid="contact-name-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">{contactContent.form.email_label}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={contactContent.form.email_placeholder}
                    required
                    className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-600 focus:border-sky-400"
                    data-testid="contact-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-300">{contactContent.form.phone_label}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={contactContent.form.phone_placeholder}
                    className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-600 focus:border-sky-400"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-300">{contactContent.form.message_label}</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={contactContent.form.message_placeholder}
                  rows={5}
                  required
                  className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-600 focus:border-sky-400 resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {contactContent.form.submit_button}
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-600 text-center">
                {contactContent.form.privacy_text}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
